import React, { useRef, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { Box, InputBase, Button, IconButton, useMediaQuery } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useStateValue } from "../../context/global-context-provider";
import { sendChat } from "../../api/chat-api";
import { useToast } from "../toaster";

const ChatContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "column",
  width: `calc(100% - ${theme.spacing(0)})`,
  maxWidth: 800,
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: "#ffff",
  boxSizing: "border-box",
}));

const ChatInputField = styled(InputBase)(() => ({
  flex: 1,
  color: "#000000",
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
  },
  lineHeight: 1.4,
  resize: "none",
  overflow: "auto",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  "& .MuiInputBase-inputMultiline": { padding: 0 },
  "& textarea::placeholder": { color: "#8e8e93" },
}));

const ToolContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
}));

const commonDisabled = {
  "&.Mui-disabled": {
    pointerEvents: "none",
    opacity: 0.6,
  },
};

const SendBtn = styled(Button)(() => ({
  marginLeft: 12,
  backgroundColor: "#D5BB4B",
  color: "#ffffff",
  textTransform: "none",
  borderRadius: 12,
  padding: "6px 16px",
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
  },
  "&:hover": { backgroundColor: "#c4ab39" },
  ...commonDisabled,
}));

const SendIconBtn = styled(IconButton)(() => ({
  marginLeft: 12,
  backgroundColor: "#D5BB4B",
  color: "#ffffff",
  borderRadius: 12,
  width: 44,
  height: 44,
  padding: 0,
  "&:hover": { backgroundColor: "#c4ab39" },
  ...commonDisabled,
}));

export default function ChatInput() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showToast } = useToast();
  const [{ loading, assistantTyping }, dispatch] = useStateValue();

  // controlled input so we can disable when empty
  const [text, setText] = useState("");
  // immediate lock to prevent double submit before global flags flip
  const lockRef = useRef(false);

  const isDisabled = loading || assistantTyping || lockRef.current || text.trim().length === 0;

  const sendMessage = async () => {
    if (isDisabled) return;
    // hard lock immediately
    lockRef.current = true;

    const message = text.trim();
    // optimistic add of user message
    dispatch({
      type: "SET_CHAT_DATA",
      payload: { content: message, role: "user", messageType: "text" },
    });
    // clear box right away
    setText("");
    // set global loading so other parts of app reflect busy state
    dispatch({ type: "SET_LOADING", loading: true });

    try {
      const response = await sendChat({ message });
      dispatch({
        type: "SET_CHAT_DATA",
        payload: response?.data?.data?.messages ?? [],
      });
    } catch (error) {
      showToast(`${error?.response?.data?.message || "Error sending message"}`, "error");
      // optional rollback if your reducer supports it
      dispatch({ type: "ROLLBACK_LAST_USER_MESSAGE" });
    } finally {
      dispatch({ type: "SET_LOADING", loading: false });
      lockRef.current = false; // release lock after global state settles
    }
  };

  const handleKeyDown = (e) => {
    if ((loading || assistantTyping || lockRef.current) && e.key === "Enter") {
      // fully block Enter when busy
      e.preventDefault();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isDisabled) return;
    sendMessage();
  };

  return (
    <ChatContainer component="form" onSubmit={handleSubmit}>
      <ChatInputField
        placeholder="type your answer here"
        multiline
        minRows={1}
        maxRows={4}
        onKeyDown={handleKeyDown}
        inputProps={{ "aria-label": "chat input" }}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <ToolContainer>
        {isMobile ? (
          <SendIconBtn type="submit" aria-label="send" disabled={isDisabled}>
            <ArrowUpwardIcon />
          </SendIconBtn>
        ) : (
          <SendBtn type="submit" disabled={isDisabled} startIcon={<ArrowUpwardIcon />}>
            {assistantTyping ? "Assistant typing..." : "Send"}
          </SendBtn>
        )}
      </ToolContainer>
    </ChatContainer>
  );
}